'use client';

import React from 'react';
import {
  Users,
  CheckCircle2,
  FileWarning,
  ShieldAlert,
  Clock,
  AlertCircle,
} from 'lucide-react';
import type { DashboardMetrics } from '@/app/admin-dashboard/components/AdminDashboardPageClient';
import Icon from '@/components/ui/AppIcon';


interface MetricsBentoGridProps {
  metrics?: DashboardMetrics;
  loading?: boolean;
}

const variantStyles: Record<string, { card: string; icon: string; value: string }> = {
  default: {
    card: 'bg-card border-border',
    icon: 'bg-primary/10 text-primary',
    value: 'text-foreground',
  },
  success: {
    card: 'bg-card border-green-200',
    icon: 'bg-green-50 text-green-600',
    value: 'text-green-700',
  },
  info: {
    card: 'bg-card border-blue-200',
    icon: 'bg-blue-50 text-blue-600',
    value: 'text-blue-700',
  },
  alert: {
    card: 'bg-red-50/60 border-red-200',
    icon: 'bg-red-100 text-red-600',
    value: 'text-red-700',
  },
  warning: {
    card: 'bg-amber-50/60 border-amber-200',
    icon: 'bg-amber-100 text-amber-600',
    value: 'text-amber-700',
  },
};

interface MetricCardProps {
  label: string;
  value: number;
  sub: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  variant?: 'default' | 'alert' | 'warning' | 'success' | 'info';
  loading?: boolean;
}

function MetricCard({ label, value, sub, icon: Icon, variant = 'default', loading }: MetricCardProps) {
  const styles = variantStyles[variant];
  return (
    <div className={`rounded-xl border p-5 shadow-card ${styles.card} transition-all duration-200 hover:shadow-elevated`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${styles.icon}`}>
          <Icon size={18} />
        </div>
      </div>
      {loading ? (
        <div className="h-9 w-12 bg-muted animate-pulse rounded mb-1" />
      ) : (
        <p className={`text-3xl font-bold tabular-nums ${styles.value}`}>{value}</p>
      )}
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-1">{label}</p>
      <p className="text-xs text-muted-foreground mt-1">{sub}</p>
    </div>
  );
}

export default function MetricsBentoGrid({ metrics, loading }: MetricsBentoGridProps) {
  const m = metrics || {
    totalStudents: 0,
    completeSubmissions: 0,
    pendingReview: 0,
    rejectedDocs: 0,
    incompleteSubmissions: 0,
    followUpRequired: 0,
  };

  const completionRate = m.totalStudents > 0
    ? Math.round((m.completeSubmissions / m.totalStudents) * 100)
    : 0;

  const cards: MetricCardProps[] = [
    {
      label: 'Total Students',
      value: m.totalStudents,
      sub: m.totalStudents === 0 ? 'No students registered yet' : `${m.totalStudents} registered`,
      icon: Users,
      variant: 'default',
    },
    {
      label: 'Complete Submissions',
      value: m.completeSubmissions,
      sub: `${completionRate}% completion rate`,
      icon: CheckCircle2,
      variant: 'success',
    },
    {
      label: 'Pending Review',
      value: m.pendingReview,
      sub: m.pendingReview === 0 ? 'No documents awaiting action' : `${m.pendingReview} awaiting review`,
      icon: Clock,
      variant: 'info',
    },
    {
      label: 'Rejected Documents',
      value: m.rejectedDocs,
      sub: m.rejectedDocs === 0 ? 'No rejections' : `${m.rejectedDocs} rejected`,
      icon: ShieldAlert,
      variant: 'alert',
    },
    {
      label: 'Incomplete Submissions',
      value: m.incompleteSubmissions,
      sub: m.incompleteSubmissions === 0 ? 'No incomplete submissions' : `${m.incompleteSubmissions} incomplete`,
      icon: FileWarning,
      variant: 'warning',
    },
    {
      label: 'Follow-Up Required',
      value: m.followUpRequired,
      sub: m.followUpRequired === 0 ? 'No follow-ups due' : `${m.followUpRequired} need follow-up`,
      icon: AlertCircle,
      variant: 'warning',
    },
  ];

  return (
    <div className="mt-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 2xl:grid-cols-6 gap-4">
      {cards.map((card) => (
        <MetricCard key={card.label} {...card} loading={loading} />
      ))}
    </div>
  );
}